import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plug, RefreshCw } from "lucide-react";
import { EnvBadge, ConnStatus } from "./EnvBadge";

const inputCls = "bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30 w-full";
const MARKETS = [["US", "United States"], ["CA", "Canada"], ["EU", "European Union"]];

function fmt(ts) { return ts ? new Date(ts).toLocaleString() : "—"; }

function minutesLeft(ts) {
    if (!ts) return null;
    const m = Math.round((new Date(ts) - Date.now()) / 60000);
    return m > 0 ? `${m} min` : "expired";
}

export function SiteCentricAgentCard({ clientId, onChange }) {
    const [cfg, setCfg] = useState(null);
    const [audit, setAudit] = useState([]);
    const [busy, setBusy] = useState(false);
    const [testRes, setTestRes] = useState(null);

    const load = async () => {
        try {
            const [{ data }, { data: a }] = await Promise.all([
                api.get(`/admin/clients/${clientId}/sitecentric-agent`),
                api.get(`/admin/clients/${clientId}/sitecentric-agent/audit`, { params: { limit: 8 } }),
            ]);
            setCfg(data);
            setAudit(a.items || []);
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    };
    useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

    const save = async (patch) => {
        setBusy(true);
        try {
            const { data } = await api.put(`/admin/clients/${clientId}/sitecentric-agent`, patch);
            setCfg(data);
            setTestRes(null);
            toast.success("SiteCentric access updated");
            onChange?.(data);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    const test = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/clients/${clientId}/sitecentric-agent/test`);
            setTestRes(data);
            setCfg((c) => ({ ...c, ...data.config, keys: c?.keys }));
            data.ok ? toast.success(data.message) : toast.error(data.error);
            onChange?.(data.config);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (!cfg) return <div className="text-xs text-white/40 font-mono" data-testid="sc-agent-loading">Loading…</div>;
    const key = cfg.keys?.find((k) => k.id === cfg.api_key_id);
    return (
        <div className="space-y-4" data-testid="sc-agent-card">
            <div className="bg-[#050505] border border-white/10 rounded-md p-4 flex items-center gap-4">
                <Plug className="w-5 h-5 text-white/60" />
                <div className="flex-1">
                    <div className="text-sm text-white/90 flex items-center gap-2">SiteCentric agent access
                        <span className="text-[9px] uppercase tracking-wider border border-amber-400/40 text-amber-300 rounded px-1 py-px" data-testid="sc-agent-legacy-badge">legacy</span>
                    </div>
                    <div className="text-[11px] text-white/45">Lets the agent obtain a temporary SiteCentric session for this client by exchanging its AIService API key at the backend of its market and environment. New flows and agents should use a Connection with the “SiteCentric · client key” auth type instead.</div>
                </div>
                <ConnStatus status={cfg.status} testId="sc-agent-status" />
                <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="sc-agent-enabled-label">
                    <input type="checkbox" checked={!!cfg.enabled} disabled={busy} onChange={(e) => save({ enabled: e.target.checked })} data-testid="sc-agent-enabled" className="accent-[#7C3AED] w-4 h-4" />
                    {cfg.enabled ? "Enabled" : "Disabled"}
                </label>
            </div>

            <div className="grid grid-cols-3 gap-3">
                <div><div className="label-xs mb-1.5">Market</div>
                    <select value={cfg.market} disabled={busy} onChange={(e) => save({ market: e.target.value })} data-testid="sc-agent-market" className={inputCls}>
                        {MARKETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select></div>
                <div><div className="label-xs mb-1.5">Environment <span className="text-white/35 normal-case tracking-normal font-normal">· default {cfg.default_environment}</span></div>
                    <select value={cfg.environment} disabled={busy} onChange={(e) => save({ environment: e.target.value })} data-testid="sc-agent-environment" className={inputCls}>
                        {(cfg.allowed_environments || []).map((e) => <option key={e} value={e}>{e}</option>)}
                    </select></div>
                <div><div className="label-xs mb-1.5">Backend</div>
                    <div className="flex items-center gap-2 h-[38px]"><code className="text-xs text-white/80 font-mono truncate" data-testid="sc-agent-backend-url">{cfg.backend_url}</code><EnvBadge environment={cfg.environment} testId="sc-agent-env-badge" /></div></div>
            </div>

            <div className="grid grid-cols-3 gap-3 items-end">
                <div className="col-span-2"><div className="label-xs mb-1.5">API key presented to SiteCentric</div>
                    <select value={cfg.api_key_id || ""} disabled={busy} onChange={(e) => save({ api_key_id: e.target.value || null })} data-testid="sc-agent-key" className={inputCls}>
                        <option value="">— select one of this client's keys —</option>
                        {(cfg.keys || []).map((k) => <option key={k.id} value={k.id}>{k.prefix}… · {k.status}{k.agent_ready ? "" : " · rotate to enable"}</option>)}
                    </select>
                    {key && !key.agent_ready && <div className="text-[11px] text-amber-200 mt-1" data-testid="sc-agent-key-hint">This key predates encrypted key storage — rotate it once (Keys tab) so the agent can present it.</div>}
                </div>
                <button onClick={test} disabled={busy || !cfg.api_key_id} data-testid="sc-agent-test"
                    className="bg-white text-black px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-40 flex items-center justify-center gap-2">
                    <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Test connection
                </button>
            </div>

            <div className={`rounded-md border px-4 py-3 text-xs font-mono ${cfg.status === "broken" ? "border-red-400/40 bg-red-400/5 text-red-200" : "border-white/10 bg-[#050505] text-white/70"}`} data-testid="sc-agent-statusline">
                {testRes && <div className="mb-1 text-white/90" data-testid="sc-agent-test-result">{testRes.ok ? testRes.message : `Test failed — ${testRes.error}`}</div>}
                <div>Backend {cfg.backend_url} · last session {fmt(cfg.last_session_at)}{cfg.last_session_expires_at ? ` · expires in ${minutesLeft(cfg.last_session_expires_at)}` : ""}</div>
                {cfg.last_error && <div className="mt-1" data-testid="sc-agent-last-error">Last error ({fmt(cfg.last_error_at)}): {cfg.last_error}</div>}
            </div>

            {audit.length > 0 && (
                <div data-testid="sc-agent-audit">
                    <div className="label-xs mb-1.5">Recent activity</div>
                    <div className="border border-white/10 rounded-md divide-y divide-white/5">
                        {audit.map((r) => (
                            <div key={r.id} className="px-3 py-1.5 text-[11px] font-mono flex gap-3 text-white/60">
                                <span className="text-white/35 w-[150px] shrink-0">{fmt(r.at)}</span>
                                <span className={r.ok ? "text-emerald-300" : "text-red-300"}>{r.event}</span>
                                <span className="truncate">{r.method ? `${r.method} ${r.path}` : ""}{r.http_status ? ` → ${r.http_status}` : ""}{r.error ? ` · ${r.error}` : ""}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
